import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "../components/Button";
import { Dialog, clearDialogFieldError, validateDialogForm } from "../components/Dialog";
import { registerNavigationGuard } from "../routing/useLocationPath";

/** Page presentation of the existing form controls and validation. */
export function SettingsForm({ children, label, description, dirty, busy, ready = true, submitLabel, onSubmit, footerActions, status, workspaceNavigation, afterFooter }: {
  workspaceNavigation?: ReactNode; children: ReactNode; label: string; description?: ReactNode; dirty: boolean; busy: boolean;
  status?: ReactNode; footerActions?: ReactNode; afterFooter?: ReactNode; ready?: boolean; submitLabel: string; onSubmit(): Promise<boolean>;
}) {
  const state = useRef({ dirty, busy }); state.current = { dirty, busy };
  const [pending, setPending] = useState<(() => void) | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (!dirty && !busy) return;
    return registerNavigationGuard(proceed => {
      if (state.current.busy) return;
      if (!state.current.dirty) { proceed(); return; }
      returnFocus.current = document.activeElement as HTMLElement;
      setPending(() => proceed);
    });
  }, [dirty, busy]);
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (state.current.dirty || state.current.busy) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, []);
  function keepEditing() {
    setPending(null);
    requestAnimationFrame(() => {
      const target = returnFocus.current;
      if (target?.isConnected && target !== document.body && target !== document.documentElement && target.getClientRects().length) target.focus();
      else [...(formRef.current?.querySelectorAll<HTMLElement>(".input, .textarea, [contenteditable=\"true\"][role=\"textbox\"]") ?? [])].find(field => field.getClientRects().length > 0)?.focus();
    });
  }
  const footer = <footer className={`settings-form-footer${footerActions ? " settings-form-footer--actions" : ""}`}>{status!==undefined?<div className="compose-save-state">{status}</div>:<span className="fld-hint">{dirty ? "Unsaved changes" : ""}</span>}<div className="settings-form-actions">{footerActions}<Button appearance="dialog" type="submit" loading={busy} disabled={!ready}>{submitLabel}</Button></div></footer>;
  return <>
    <form ref={formRef} className="settings-form" aria-label={label} noValidate aria-busy={busy || undefined}
      onInput={event => clearDialogFieldError(event.target as HTMLElement)}
      onSubmit={async event => { event.preventDefault(); if (!busy && ready && validateDialogForm(event.currentTarget)) await onSubmit(); }}>
      {description ? <p className="settings-description">{description}</p> : null}
      {workspaceNavigation?<><div className="resolved-compose-surface">{workspaceNavigation}<div className="settings-form-fields">{children}</div></div>{footer}</>:<><div className="settings-form-fields">{children}</div>{footer}</>}{afterFooter}
    </form>
    {pending ? <Dialog label="Discard unsaved changes?" description="Your saved settings will stay unchanged." dirty={false} busy={false} submitLabel="Discard changes" showCancel={false} size="sm"
      restoreFocusRef={returnFocus} onClose={keepEditing} onSubmit={async () => { pending(); return true; }}>
      <Button appearance="dialog" variant="quiet" autoFocus onClick={keepEditing}>Keep editing</Button>
    </Dialog> : null}
  </>;
}
